/**
 * GameOverOverlay — full-screen end-of-match panel.
 *
 * Renders when `gameState.outerPhase === 'ended'`. Shows the winner
 * (their name, colour swatch, and territory count) plus a CTA to
 * return home.
 *
 * Reads exclusively from the GameState snapshot — no local state.
 */

import { type ReactNode, useMemo } from 'react';
import { motion } from 'motion/react';
import type { GameState, PlayerId } from '@qd/protocol';
import { fontStacks, playerColors, surfaces } from '@qd/design-tokens';
import { ChalkButton } from '../base/ChalkButton';
import { useIsMobile } from '../../hooks/useIsMobile';

interface GameOverOverlayProps {
  readonly gameState: GameState;
  readonly onReturnHome: () => void;
}

export function GameOverOverlay({ gameState, onReturnHome }: GameOverOverlayProps): ReactNode {
  const isMobile = useIsMobile();
  const winner = useMemo(() => {
    if (!gameState.winnerId) return null;
    return gameState.players.find((p) => p.id === gameState.winnerId) ?? null;
  }, [gameState]);

  const territoryCount = useMemo(
    () => countTerritories(gameState, gameState.winnerId),
    [gameState],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="qd-game-over-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(50, 42, 30, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Tighter outer padding on mobile so the card has room to breathe.
        padding: isMobile ? 12 : 24,
        // Allow the wrapper to scroll vertically if the card is taller
        // than the viewport (e.g. landscape phones, accessibility zoom).
        overflowY: 'auto',
        maxHeight: '100dvh',
      }}
    >
      <div
        style={{
          background: surfaces.paper,
          color: surfaces.ink,
          // Mobile: trim padding so the card doesn't waste 96px of side
          // padding on a 360px-wide phone. Desktop padding is unchanged.
          padding: isMobile ? '20px 16px' : '40px 48px',
          maxWidth: 520,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          textAlign: 'center',
        }}
      >
        <h1
          id="qd-game-over-title"
          style={{
            margin: 0,
            fontFamily: fontStacks.display,
            // clamp() handles long player names and small viewports
            // without awkward wrapping on the heading.
            fontSize: 'clamp(24px, 6vw, 36px)',
            letterSpacing: '0.18em',
            fontVariantCaps: 'all-small-caps',
            fontVariationSettings: '"opsz" 96, "SOFT" 50',
            color: surfaces.waxRed,
            fontWeight: 700,
          }}
        >
          Match Closed
        </h1>
        {winner ? (
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 18px',
                background: playerColors[winner.color].hex,
                color: surfaces.chalk,
                fontFamily: fontStacks.display,
                fontSize: 22,
                letterSpacing: '0.14em',
                fontVariantCaps: 'all-small-caps',
                fontWeight: 700,
                opacity: 0.92,
                borderRadius: 2,
              }}
            >
              {winner.name}
            </div>
            <p
              style={{
                marginTop: 14,
                fontFamily: fontStacks.body,
                fontSize: 14,
                color: surfaces.ink,
                opacity: 0.78,
              }}
            >
              took {territoryCount} territories under quality control.
            </p>
          </div>
        ) : (
          <p
            style={{
              marginTop: 24,
              fontFamily: fontStacks.body,
              fontSize: 16,
              color: surfaces.ink,
              opacity: 0.78,
            }}
          >
            Match ended in a stalemate.
          </p>
        )}
        <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
          <ChalkButton variant="primary" onClick={onReturnHome} ariaLabel="Return to home">
            Return to Home
          </ChalkButton>
        </div>
      </div>
    </motion.div>
  );
}

function countTerritories(state: GameState, ownerId: PlayerId | null): number {
  if (!ownerId) return 0;
  let n = 0;
  for (const t of Object.values(state.territories)) {
    if (t.controllingPlayerId === ownerId) n += 1;
  }
  return n;
}
